/**
 * The rating screen.
 *
 * One review per dining session — a second attempt returns 409, which is shown
 * as a friendly "already submitted" rather than an error, because from the
 * guest's point of view it is not a failure.
 *
 * The stars are radio buttons, not divs with onClick: a guest using VoiceOver
 * or a switch device must be able to rate the meal too.
 */

import { useState } from 'react';
import { isApiError } from '@sonauto/api-client';
import { useCustomerI18n } from './CustomerI18nProvider';
import { errorMessage } from '../lib/hooks';
import type { CustomerApi } from '../lib/api';

export function RatingScreen({
  api,
  onDone,
}: {
  readonly api: CustomerApi;
  readonly onDone: () => void;
}): React.JSX.Element {
  const { tOr } = useCustomerI18n();
  const [rating, setRating] = useState(0);
  const [food, setFood] = useState(0);
  const [service, setService] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (rating < 1) return;
    setBusy(true);
    setError(null);
    try {
      await api.feedback({
        rating,
        foodRating: food === 0 ? null : food,
        serviceRating: service === 0 ? null : service,
        speedRating: speed === 0 ? null : speed,
        comment: comment.trim() === '' ? null : comment.trim(),
      });
      setDone(true);
    } catch (cause: unknown) {
      // 409 = already reviewed. Not a failure the guest caused or can fix.
      if (isApiError(cause) && cause.status === 409) {
        setDone(true);
        return;
      }
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="px-4 py-10 text-center">
        <p aria-hidden="true" className="text-5xl">
          🙏
        </p>
        <h2 className="text-flow mt-3 text-xl font-bold">{tOr('customer.rate_us_thanks', 'Cảm ơn bạn đã đánh giá!')}</h2>
        <p className="text-flow mt-2 text-sm text-fg-muted">
          {tOr('customer.thank_you_body', 'Góp ý của bạn giúp chúng tôi phục vụ tốt hơn.')}
        </p>
        <button
          type="button"
          onClick={onDone}
          className="text-flow mt-6 min-h-[56px] w-full rounded-lg bg-brand-600 px-4 py-3 text-base font-bold text-white"
        >
          {tOr('customer.nav.done', 'Xong')}
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-4">
      <h2 className="text-flow text-lg font-bold">{tOr('customer.rate_us', 'Đánh giá trải nghiệm')}</h2>
      <p className="text-flow mt-1 text-sm text-fg-muted">
        {tOr('customer.rate_us_hint', 'Bạn thấy bữa ăn hôm nay thế nào?')}
      </p>

      <Stars label={tOr('customer.rate_us', 'Đánh giá chung')} value={rating} onChange={setRating} required />
      <Stars label={tOr('customer.item.description', 'Món ăn')} value={food} onChange={setFood} />
      <Stars label={tOr('customer.nav.help', 'Phục vụ')} value={service} onChange={setService} />
      <Stars label={tOr('customer.order.preparing', 'Tốc độ')} value={speed} onChange={setSpeed} />

      <label className="text-flow mt-5 block text-sm font-bold" htmlFor="rating-comment">
        {tOr('customer.note.label', 'Góp ý')}
      </label>
      <textarea
        id="rating-comment"
        rows={4}
        maxLength={1000}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={tOr('customer.feedback.placeholder', 'Điều gì khiến bạn hài lòng hoặc chưa hài lòng?')}
        className="mt-1 w-full rounded-md bg-surface-alt p-3 text-base ring-1 ring-line"
      />

      {error !== null ? (
        <p className="text-flow mt-3 rounded-lg bg-danger-bg p-3 text-sm font-semibold text-danger-fg">{error}</p>
      ) : null}

      <button
        type="button"
        disabled={busy || rating < 1}
        onClick={() => void submit()}
        className="text-flow mt-4 min-h-[60px] w-full rounded-lg bg-brand-600 px-4 py-3 text-lg font-bold text-white disabled:opacity-50"
      >
        {tOr('customer.rate_us_submit', 'Gửi đánh giá')}
      </button>
    </div>
  );
}

function Stars({
  label,
  value,
  onChange,
  required = false,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly required?: boolean;
}): React.JSX.Element {
  return (
    <fieldset className="mt-4">
      <legend className="text-flow text-sm font-semibold">
        {label}
        {required ? <span className="ml-1 text-danger-fg">*</span> : null}
      </legend>
      <div className="mt-1 flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <label key={star} className="cursor-pointer">
            <input
              type="radio"
              name={label}
              value={star}
              checked={value === star}
              onChange={() => onChange(star)}
              className="sr-only"
            />
            <span
              aria-hidden="true"
              className={`inline-flex h-12 w-12 items-center justify-center text-3xl ${
                star <= value ? 'text-warning-solid' : 'text-fg-subtle'
              }`}
            >
              ★
            </span>
            <span className="sr-only">{star}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
